import { Box } from "@itandi/itandi-bb-ui/Box";
import { Label } from "@itandi/itandi-bb-ui/Label";
import { PresentationChip } from "@itandi/itandi-bb-ui/PresentationChip";
import type React from "react";
import type { ReactNode } from "react";
import type { DealConstruction } from "~/models/DealConstruction";
import {
	ESTIMATION_TYPE,
	getConstructionItemName
} from "~/models/DealConstruction";
import type { DealEstimationHistoryContext } from "~/models/DealEstimationHistory";
import { toThousandSeparator } from "~/utils/CurrencySeparator";
import { isEmpty } from "~/utils/StringUtil";
import { ConstructionAmountRow } from "../list/ConstructionAmountRow";
import Styles from "../list/ConstructionList.module.scss";
import { ConstructionListFooterRow } from "../list/ConstructionListFooterRow";
import { FeeRow } from "../list/FeeRow";

type Props = {
	companyType: "client" | "vendor" | "partner";
	constructionContext: DealEstimationHistoryContext;
};

export const ConstructionListRow: React.FC<Props> = ({
	constructionContext,
	companyType
}: Props) => {
	const { dealConstructions } = constructionContext;
	const hasVendorFee = constructionContext.vendorFee !== 0;
	const renderRow = (dealConstruction: DealConstruction): ReactNode => (
		<tr className={Styles.ListRow} key={dealConstruction.id}>
			<td className={Styles.OrderHandle} />
			<td className={Styles.EstimationType}>
				{dealConstruction.estimationType === ESTIMATION_TYPE.PROPOSAL && (
					<PresentationChip variant="blue">提案</PresentationChip>
				)}
			</td>
			<td className={Styles.Estimation}>
				<div className={Styles.SPEstimationType}>
					{dealConstruction.estimationType === ESTIMATION_TYPE.PROPOSAL && (
						<PresentationChip variant="blue">提案</PresentationChip>
					)}
				</div>
				<div className={Styles.EstimationConfirmedPointAndItem}>
					{!isEmpty(dealConstruction.point) && (
						<div className={Styles.Point}>{dealConstruction.point}</div>
					)}
					{getConstructionItemName(dealConstruction)}
				</div>
				{!isEmpty(dealConstruction.note) && (
					<Box marginTop=".25rem">
						<Label size="xs">{`備考）${dealConstruction.note}`}</Label>
					</Box>
				)}
			</td>
			<td className={Styles.UnitAmount}>
				<p>
					¥{toThousandSeparator(dealConstruction.unitAmount)} *{" "}
					{toThousandSeparator(dealConstruction.unit)}
					{dealConstruction.unitName}
				</p>
			</td>
			<td className={Styles.ChargePer}>
				<p>
					借主{dealConstruction.renteeChargeWithPercentage} : 貸主
					{dealConstruction.ownerChargeWithPercentage} : 管理
					{dealConstruction.companyChargeWithPercentage}
				</p>
			</td>
			<td className={Styles.Amount}>
				<p>¥{toThousandSeparator(dealConstruction.totalAmount)}</p>
			</td>
		</tr>
	);
	return (
		<>
			{dealConstructions.map(renderRow)}
			{dealConstructions.length > 0 &&
				constructionContext.managementFee !== 0 && (
					<FeeRow
						docType="estimation"
						fee={constructionContext.managementFee}
						feeTitle="諸経費（工事管理費）"
					/>
				)}
			{companyType === "vendor" && hasVendorFee ? (
				<ConstructionAmountRow
					constructionContext={constructionContext}
					docType="estimation"
				/>
			) : null}
			{dealConstructions.length > 0 &&
			companyType !== "client" &&
			hasVendorFee ? (
				<FeeRow
					docType="estimation"
					fee={constructionContext.vendorFee}
					feeTitle="うち送客手数料"
				/>
			) : null}
			<ConstructionListFooterRow
				companyType={companyType}
				constructionContext={constructionContext}
				docType="estimation"
				hasVendorFee={hasVendorFee}
				showButtons={false}
				target={null}
			/>
		</>
	);
};
